import { useEffect, useRef, useCallback } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import type { TrackedCell } from "@/hooks/useScanEvents";

interface ScanCircleOverlayProps {
  activeCells: Map<string, TrackedCell>;
  isScanning: boolean;
  maxVisibleCompleted?: number;
}

const MAX_COMPLETED_DEFAULT = 15;
const FADE_DELAY_MS = 2000;
const FADE_DURATION_MS = 500;
const RIPPLE_INTERVAL_MS = 1200;
const RIPPLE_DURATION_MS = 1500;
const MAX_RIPPLES_PER_CELL = 3;

const COLORS = {
  scanning: { fill: "#3b82f6", stroke: "#2563eb" },
  completed: { fill: "#22c55e", stroke: "#16a34a" },
  failed: { fill: "#ef4444", stroke: "#dc2626" },
};

export function ScanCircleOverlay({
  activeCells,
  isScanning,
  maxVisibleCompleted = MAX_COMPLETED_DEFAULT,
}: ScanCircleOverlayProps) {
  const map = useMap();

  const circlesRef = useRef(new Map<string, google.maps.Circle>());
  const rippleRingsRef = useRef(new Map<string, google.maps.Circle[]>());
  const rippleIntervalsRef = useRef(new Map<string, ReturnType<typeof setInterval>>());
  const fadeTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const completedOrderRef = useRef<string[]>([]);
  const prevStatusRef = useRef(new Map<string, string>());
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const removeCircle = useCallback((cellId: string) => {
    const circle = circlesRef.current.get(cellId);
    if (circle) {
      circle.setMap(null);
      circlesRef.current.delete(cellId);
    }
    // Clean up ripples
    const interval = rippleIntervalsRef.current.get(cellId);
    if (interval) {
      clearInterval(interval);
      rippleIntervalsRef.current.delete(cellId);
    }
    const rings = rippleRingsRef.current.get(cellId);
    if (rings) {
      for (const ring of rings) ring.setMap(null);
      rippleRingsRef.current.delete(cellId);
    }
    // Clean up fade timer
    const fadeTimer = fadeTimersRef.current.get(cellId);
    if (fadeTimer) {
      clearTimeout(fadeTimer);
      fadeTimersRef.current.delete(cellId);
    }
    prevStatusRef.current.delete(cellId);
  }, []);

  const fadeOutCircle = useCallback((cellId: string, onComplete?: () => void) => {
    const circle = circlesRef.current.get(cellId);
    if (!circle) {
      onComplete?.();
      return;
    }

    if (prefersReducedMotion.current) {
      removeCircle(cellId);
      onComplete?.();
      return;
    }

    const startFill = circle.get("fillOpacity") ?? 0.15;
    const startStroke = circle.get("strokeOpacity") ?? 0.6;
    const startTime = performance.now();

    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / FADE_DURATION_MS, 1);
      const eased = progress * progress; // ease-in

      circle!.setOptions({
        fillOpacity: startFill * (1 - eased),
        strokeOpacity: startStroke * (1 - eased),
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        removeCircle(cellId);
        onComplete?.();
      }
    }

    requestAnimationFrame(animate);
  }, [removeCircle]);

  const startRipple = useCallback((cellId: string, center: google.maps.LatLngLiteral, radius: number) => {
    if (prefersReducedMotion.current) return;
    if (!map) return;

    // Stop existing ripple for this cell
    const existingInterval = rippleIntervalsRef.current.get(cellId);
    if (existingInterval) clearInterval(existingInterval);

    const rings: google.maps.Circle[] = [];
    rippleRingsRef.current.set(cellId, rings);

    const spawnRing = () => {
      if (!circlesRef.current.has(cellId)) {
        // Cell was removed, stop
        const iv = rippleIntervalsRef.current.get(cellId);
        if (iv) clearInterval(iv);
        return;
      }

      // Evict oldest ring if at max
      if (rings.length >= MAX_RIPPLES_PER_CELL) {
        const oldest = rings.shift();
        oldest?.setMap(null);
      }

      const ring = new google.maps.Circle({
        center,
        radius: radius * 0.3,
        fillOpacity: 0,
        strokeColor: COLORS.scanning.stroke,
        strokeOpacity: 0.5,
        strokeWeight: 1.5,
        clickable: false,
        map,
      });
      rings.push(ring);

      const startTime = performance.now();
      const startRadius = radius * 0.3;
      const endRadius = radius * 1.2;

      function animateRing(now: number) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / RIPPLE_DURATION_MS, 1);

        const currentRadius = startRadius + (endRadius - startRadius) * progress;
        const opacity = 0.5 * (1 - progress);

        ring.setOptions({
          radius: currentRadius,
          strokeOpacity: opacity,
        });

        if (progress < 1) {
          requestAnimationFrame(animateRing);
        } else {
          ring.setMap(null);
          const idx = rings.indexOf(ring);
          if (idx !== -1) rings.splice(idx, 1);
        }
      }

      requestAnimationFrame(animateRing);
    };

    // Spawn first ring immediately, then on interval
    spawnRing();
    const intervalId = setInterval(spawnRing, RIPPLE_INTERVAL_MS);
    rippleIntervalsRef.current.set(cellId, intervalId);
  }, [map]);

  const stopRipple = useCallback((cellId: string) => {
    const interval = rippleIntervalsRef.current.get(cellId);
    if (interval) {
      clearInterval(interval);
      rippleIntervalsRef.current.delete(cellId);
    }
    const rings = rippleRingsRef.current.get(cellId);
    if (rings) {
      for (const ring of rings) ring.setMap(null);
      rippleRingsRef.current.delete(cellId);
    }
  }, []);

  // Main sync effect
  useEffect(() => {
    if (!map) return;

    const currentCellIds = new Set(activeCells.keys());

    // 1. Remove circles for cells no longer in state
    for (const cellId of circlesRef.current.keys()) {
      if (!currentCellIds.has(cellId)) {
        removeCircle(cellId);
      }
    }

    // 2. Create/update circles
    for (const [cellId, cell] of activeCells) {
      const existingCircle = circlesRef.current.get(cellId);
      const prevStatus = prevStatusRef.current.get(cellId);

      if (!existingCircle) {
        // New cell — create circle
        const colors = COLORS[cell.status];
        const circle = new google.maps.Circle({
          center: { lat: cell.centerLat, lng: cell.centerLng },
          radius: cell.radius,
          fillColor: colors.fill,
          fillOpacity: 0.15,
          strokeColor: colors.stroke,
          strokeOpacity: 0.6,
          strokeWeight: 1.5,
          clickable: false,
          map,
        });
        circlesRef.current.set(cellId, circle);
        prevStatusRef.current.set(cellId, cell.status);

        // Start ripple for scanning cells
        if (cell.status === "scanning") {
          startRipple(cellId, { lat: cell.centerLat, lng: cell.centerLng }, cell.radius);
        }

        // If already completed/failed when first seen, schedule fade
        if (cell.status === "completed" || cell.status === "failed") {
          completedOrderRef.current.push(cellId);
          enforceCap();
          scheduleFade(cellId);
        }
      } else if (prevStatus === "scanning" && cell.status !== "scanning") {
        // Cell transitioned from scanning to completed/failed
        stopRipple(cellId);

        const colors = COLORS[cell.status];
        existingCircle.setOptions({
          fillColor: colors.fill,
          strokeColor: colors.stroke,
        });
        prevStatusRef.current.set(cellId, cell.status);

        completedOrderRef.current.push(cellId);
        enforceCap();
        scheduleFade(cellId);
      }
    }

    function scheduleFade(cellId: string) {
      if (prefersReducedMotion.current) {
        // No animation — remove after display delay
        const timer = setTimeout(() => {
          removeCircle(cellId);
          const idx = completedOrderRef.current.indexOf(cellId);
          if (idx !== -1) completedOrderRef.current.splice(idx, 1);
        }, FADE_DELAY_MS);
        fadeTimersRef.current.set(cellId, timer);
        return;
      }

      const timer = setTimeout(() => {
        fadeOutCircle(cellId, () => {
          const idx = completedOrderRef.current.indexOf(cellId);
          if (idx !== -1) completedOrderRef.current.splice(idx, 1);
        });
        fadeTimersRef.current.delete(cellId);
      }, FADE_DELAY_MS);
      fadeTimersRef.current.set(cellId, timer);
    }

    function enforceCap() {
      while (completedOrderRef.current.length > maxVisibleCompleted) {
        const oldest = completedOrderRef.current.shift()!;
        // Cancel pending fade timer and remove immediately
        const timer = fadeTimersRef.current.get(oldest);
        if (timer) {
          clearTimeout(timer);
          fadeTimersRef.current.delete(oldest);
        }
        removeCircle(oldest);
      }
    }
  }, [map, activeCells, maxVisibleCompleted, removeCircle, fadeOutCircle, startRipple, stopRipple]);

  // Auto-hide all circles when scan phase ends
  const prevIsScanning = useRef(isScanning);
  useEffect(() => {
    if (prevIsScanning.current && !isScanning) {
      // Scan just ended — stagger-fade all remaining circles
      const remaining = Array.from(circlesRef.current.keys());
      remaining.forEach((cellId, index) => {
        // Cancel any existing fade timers
        const timer = fadeTimersRef.current.get(cellId);
        if (timer) {
          clearTimeout(timer);
          fadeTimersRef.current.delete(cellId);
        }
        stopRipple(cellId);

        const delay = prefersReducedMotion.current ? 0 : index * 50;
        const newTimer = setTimeout(() => {
          fadeOutCircle(cellId);
          fadeTimersRef.current.delete(cellId);
        }, delay);
        fadeTimersRef.current.set(cellId, newTimer);
      });
      completedOrderRef.current = [];
    }
    prevIsScanning.current = isScanning;
  }, [isScanning, fadeOutCircle, stopRipple]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const cellId of circlesRef.current.keys()) {
        removeCircle(cellId);
      }
      circlesRef.current.clear();
      rippleRingsRef.current.clear();
      prevStatusRef.current.clear();
      completedOrderRef.current = [];
    };
  }, [removeCircle]);

  return null;
}
